import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import WelcomeScreen from './components/WelcomeScreen';
import SurveyQuestions from './components/SurveyQuestions';
import ResultsPage from './components/ResultsPage';
import LoginScreen from './components/LoginScreen';
import SignupScreen from './components/SignupScreen';
import SocialCallback from './components/SocialCallback';
import PeerSurveyPage from './components/PeerSurveyPage';
import ComparisonPage from './components/ComparisonPage';
import Dashboard from './components/Dashboard';
import { getCurrentUser, logout, submitSurvey as submitSurveyAPI, getSurveyResponses } from './services/api';
import { calculateScores } from './services/scoringService';
import './styles/main.css';

function App() {
  const [surveyResponses, setSurveyResponses] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [dashboardOriginRoute, setDashboardOriginRoute] = useState('/welcome');
  const navigate = useNavigate();
  const location = useLocation();

  // Allowed emails for dashboard access
  const DASHBOARD_ALLOWED_EMAILS = ['ipistela@profit-circles.com', 'info@profit-circles.com'];

  const isUserDashboardAuthorized = () => {
    return user && DASHBOARD_ALLOWED_EMAILS.includes(user.email);
  };

  const openDashboard = () => {
    setDashboardOriginRoute(location.pathname);
    navigate('/dashboard');
  };

  // Auto-hide header on survey page
  useEffect(() => {
    if (location.pathname === '/survey') {
      setHeaderVisible(false);
    } else {
      setHeaderVisible(true);
    }
  }, [location.pathname]);

  // Check if this is a peer survey route - show it regardless of auth state
  const isPeerSurveyPath = location.pathname.startsWith('/peer-survey/');

  // Check if user is already logged in and fetch existing results (skip for peer surveys)
  useEffect(() => {
    if (isPeerSurveyPath) {
      // Skip all auth/loading logic for peer surveys
      setLoading(false);
      return;
    }

    // Check if we should skip auth for development
    const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';
    
    if (skipAuth) {
      // Set demo admin user for development
      const demoUser = {
        id: 1,
        email: 'admin@example.com',
        name: 'Admin User',
        role: 'super_admin'
      };
      localStorage.setItem('authToken', 'demo-token-dev');
      localStorage.setItem('user', JSON.stringify(demoUser));
      setUser(demoUser);
      setLoading(false);
      navigate('/welcome');
      return;
    }
    
    const storedUser = getCurrentUser();
    if (storedUser) {
      setUser(storedUser);
      
      // Try to fetch existing survey results
      fetchUserResults();
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPeerSurveyPath]);

  const fetchUserResults = async () => {
    try {
      // Get the survey responses from backend
      const responsesData = await getSurveyResponses();
      const responses = responsesData.responses;
      
      if (responses && Object.keys(responses).length > 0) {
        // Convert responses to array format that ResultsPage expects
        const responsesArray = {};
        Object.entries(responses).forEach(([questionId, value]) => {
          responsesArray[questionId] = value;
        });
        setSurveyResponses(responsesArray);
        navigate('/results');
      } else {
        navigate('/welcome');
      }
    } catch (err) {
      // No results found, show welcome screen
      navigate('/welcome');
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    fetchUserResults();
  };

  const handleSignupSuccess = (userData) => {
    setUser(userData);
    navigate('/welcome');
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    setSurveyResponses(null);
    navigate('/login');
  };

  const handleStartSurvey = () => {
    navigate('/survey');
    window.scrollTo(0, 0);
  };

  const handleBackToResults = () => {
    navigate('/results');
    window.scrollTo(0, 0);
  };

  const handleCompleteSurvey = async (responses) => {
    setSurveyResponses(responses);
    
    // Calculate scores
    const scores = calculateScores(responses);
    
    // Submit to backend
    try {
      await submitSurveyAPI(responses, scores);
    } catch (err) {
      console.error('Failed to save survey to backend:', err);
    }
    
    navigate('/results');
    window.scrollTo(0, 0);
  };

  const handleRestartSurvey = () => {
    navigate('/welcome');
    window.scrollTo(0, 0);
  };

  if (loading) {
    return <div className="app"><div className="loading">Loading...</div></div>;
  }

  // Check if this is a peer survey route - show it regardless of auth state
  if (isPeerSurveyPath) {
    return (
      <div className="app">
        <Routes>
          <Route path="/peer-survey/:token" element={<PeerSurveyPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app">
        <Routes>
          <Route path="/login" element={
            <LoginScreen
              onLoginSuccess={handleLoginSuccess}
              onSwitchToSignup={() => navigate('/signup')}
            />
          } />
          <Route path="/signup" element={
            <SignupScreen
              onSignupSuccess={handleSignupSuccess}
              onBackToLogin={() => navigate('/login')}
            />
          } />
          <Route path="/auth/social/callback" element={<SocialCallback onLoginSuccess={handleLoginSuccess} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="app">
      {headerVisible && (
        <header className="app-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="app-title">Creative Work Footprint</h1>
              <p className="app-tagline">Discover Your Role in the Wheel of Work</p>
            </div>
            <div className="header-right">
              <span className="user-email">{user.email}</span>
              {isUserDashboardAuthorized() && (
                <button 
                  className="btn btn-dashboard" 
                  onClick={openDashboard}
                  title="Open Dashboard"
                >
                  Dashboard
                </button>
              )}
              <button className="btn btn-logout" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </header>
      )}

      {location.pathname === '/survey' && !headerVisible && (
        <div className="survey-header-mobile">
          <button 
            className="hamburger-menu" 
            onClick={() => setHeaderVisible(true)}
            title="Show menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      )}

      {headerVisible && location.pathname === '/survey' && (
        <div 
          className="header-overlay show" 
          onClick={() => setHeaderVisible(false)}
        />
      )}

      {headerVisible && location.pathname === '/survey' && (
        <header className="app-header survey-header-expanded">
          <div className="header-content">
            <div className="header-left">
              <h1 className="app-title">Creative Work Footprint</h1>
              <p className="app-tagline">Discover Your Role in the Wheel of Work</p>
            </div>
            <div className="header-right">
              <span className="user-email">{user.email}</span>
              {isUserDashboardAuthorized() && (
                <button 
                  className="btn btn-dashboard" 
                  onClick={openDashboard}
                  title="Open Dashboard"
                >
                  Dashboard
                </button>
              )}
              <button className="btn btn-logout" onClick={handleLogout}>Logout</button>
              <button 
                className="close-header-btn"
                onClick={() => setHeaderVisible(false)}
                title="Close menu"
              >
                ✕
              </button>
            </div>
          </div>
        </header>
      )}

      <main className={`app-main ${headerVisible && location.pathname === '/survey' ? 'with-fixed-header' : ''}`}>
        <Routes>
          <Route path="/welcome" element={<WelcomeScreen onStart={handleStartSurvey} hasPreviousResults={!!surveyResponses} onBackToResults={handleBackToResults} />} />
          <Route path="/survey" element={<SurveyQuestions onComplete={handleCompleteSurvey} />} />
          <Route path="/results" element={
            surveyResponses ? (
              <ResultsPage responses={surveyResponses} onRestart={handleRestartSurvey} />
            ) : (
              <Navigate to="/welcome" replace />
            )
          } />
          <Route path="/comparison/:userId" element={<ComparisonPage />} />
          <Route path="/dashboard" element={
            isUserDashboardAuthorized() ? (
              <Dashboard originRoute={dashboardOriginRoute} />
            ) : (
              <Navigate to="/welcome" replace />
            )
          } />
          <Route path="/" element={<Navigate to="/welcome" replace />} />
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <p>&copy; 2025 Profit-Circles. All rights reserved. | Powered by the Wheel of Work</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
